/**
 * UserProfilePage - Organization Settings & Profile Management
 *
 * A comprehensive settings page with tabs for:
 * - Settings: Organization details and preferences
 * - API Keys: Create, manage, and rotate API keys
 * - Members: Team member management and invitations
 * - Usage: Usage metrics and limits
 * - Billing: Subscription and payment management
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/AuthContext';
import {
  getOrganization,
  updateOrganization,
  getApiKeys,
  createApiKey,
  rotateApiKey,
  deleteApiKey,
  getMembers,
  getInvitations,
  inviteMember,
  updateMember,
  removeMember,
  cancelInvitation,
  getUsageSummary,
  getFeatureConfig,
} from '@/services/organizationService';
import {
  getSubscription as getStripeSubscription,
  getPlans as getStripePlans,
  getPaymentMethods as getStripePaymentMethods,
  getInvoices as getStripeInvoices,
  createCheckoutSession,
  openCustomerPortal,
  cancelSubscription as cancelStripeSubscription,
  reactivateSubscription,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  formatAmount,
  getCardBrandName,
  checkCheckoutResult,
} from '@/services/stripeService';

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Settings,
  Key,
  Users,
  BarChart3,
  CreditCard,
  ArrowLeft,
  Copy,
  Check,
  RefreshCw,
  Trash2,
  Plus,
  Mail,
  Shield,
  AlertCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Download,
  Crown,
  UserCog,
  User as UserIcon,
  Building,
  Save,
  X,
  MoreVertical,
  ChevronRight,
} from 'lucide-react';

// ============================================================================
// Tab Configuration
// ============================================================================

const TABS = [
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'api-keys', label: 'API Keys', icon: Key },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'usage', label: 'Usage', icon: BarChart3 },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(dateString, options = {}) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const defaultOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
}

function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
}

function formatNumber(num) {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

function getTimeAgo(dateString) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

function getDaysUntil(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  return Math.ceil(diffMs / 86400000);
}

function getRoleBadgeClass(role) {
  switch (role) {
    case 'owner':
      return 'bg-primary/10 text-primary border-primary/20';
    case 'admin':
      return 'bg-warning/10 text-warning border-warning/20';
    case 'member':
      return 'bg-success/10 text-success border-success/20';
    case 'viewer':
      return 'bg-muted text-muted-foreground border-border';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

function getStatusBadgeClass(status) {
  switch (status) {
    case 'active':
      return 'bg-success/10 text-success border-success/20';
    case 'pending':
      return 'bg-warning/10 text-warning border-warning/20';
    case 'expired':
    case 'revoked':
    case 'suspended':
      return 'bg-destructive/10 text-destructive border-destructive/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

// ============================================================================
// Main Component
// ============================================================================

export default function UserProfilePage({ orgId = 'current', onBack }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state
  const [organization, setOrganization] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [usage, setUsage] = useState(null);
  const [featureConfig, setFeatureConfig] = useState(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, [orgId]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [orgData, featuresData] = await Promise.all([
        getOrganization(orgId),
        getFeatureConfig(orgId),
      ]);
      setOrganization(orgData);
      setFeatureConfig(featuresData);
    } catch (err) {
      setError('Failed to load organization data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load tab-specific data
  useEffect(() => {
    if (!organization) return;

    const loadTabData = async () => {
      switch (activeTab) {
        case 'api-keys':
          if (apiKeys.length === 0) {
            const keys = await getApiKeys(orgId);
            setApiKeys(keys);
          }
          break;
        case 'members':
          if (members.length === 0) {
            const [membersData, invitesData] = await Promise.all([
              getMembers(orgId),
              getInvitations(orgId),
            ]);
            setMembers(membersData);
            setInvitations(invitesData);
          }
          break;
        case 'usage':
          if (!usage) {
            const usageData = await getUsageSummary(orgId);
            setUsage(usageData);
          }
          break;
        // Billing tab handles its own data loading via Stripe service
        case 'billing':
          break;
      }
    };
    loadTabData();
  }, [activeTab, organization, orgId]);

  // Determine if current user is admin or owner
  const currentMember = members.find((m) => m.email === user?.email);
  const isAdmin = currentMember?.role === 'owner' || currentMember?.role === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
          </div>
          <p className="mt-4 text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Settings</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={loadInitialData}>Try Again</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {onBack && (
                <Button variant="ghost" size="icon" onClick={onBack}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              )}
              <div>
                <h1 className="text-xl font-semibold text-foreground">Organization Settings</h1>
                <p className="text-sm text-muted-foreground">{organization?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building className="w-4 h-4" />
                <span>{organization?.slug}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
            {TABS.map((tab) => {
              // Check feature visibility
              if (tab.id === 'api-keys' && !featureConfig?.showApiKeys) return null;
              if (tab.id === 'members' && !featureConfig?.showMembers) return null;
              if (tab.id === 'usage' && !featureConfig?.showUsage) return null;
              if (tab.id === 'billing' && !featureConfig?.showBilling) return null;

              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.id} value={tab.id} className="gap-2">
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <SettingsTab
              organization={organization}
              setOrganization={setOrganization}
              orgId={orgId}
              isAdmin={isAdmin}
            />
          </TabsContent>

          {/* API Keys Tab */}
          {featureConfig?.showApiKeys && (
            <TabsContent value="api-keys">
              <ApiKeysTab
                apiKeys={apiKeys}
                setApiKeys={setApiKeys}
                orgId={orgId}
                featureConfig={featureConfig}
                isAdmin={isAdmin}
              />
            </TabsContent>
          )}

          {/* Members Tab */}
          {featureConfig?.showMembers && (
            <TabsContent value="members">
              <MembersTab
                members={members}
                setMembers={setMembers}
                invitations={invitations}
                setInvitations={setInvitations}
                orgId={orgId}
                currentUser={user}
                featureConfig={featureConfig}
                isAdmin={isAdmin}
              />
            </TabsContent>
          )}

          {/* Usage Tab */}
          {featureConfig?.showUsage && (
            <TabsContent value="usage">
              <UsageTab usage={usage} />
            </TabsContent>
          )}

          {/* Billing Tab */}
          {featureConfig?.showBilling && (
            <TabsContent value="billing">
              <BillingTab
                orgId={orgId}
                featureConfig={featureConfig}
                isAdmin={isAdmin}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

// ============================================================================
// Settings Tab Component
// ============================================================================

function SettingsTab({ organization, setOrganization, orgId, isAdmin }) {
  const [name, setName] = useState(organization?.name || '');
  const [description, setDescription] = useState(organization?.description || '');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    setName(organization?.name || '');
    setDescription(organization?.description || '');
  }, [organization]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    const result = await updateOrganization(orgId, { name, description });
    setSaving(false);

    if (result.success) {
      setOrganization((prev) => ({ ...prev, name, description }));
      setSaveMessage({ type: 'success', text: 'Settings saved successfully' });
    } else {
      setSaveMessage({ type: 'error', text: result.error || 'Failed to save settings' });
    }

    setTimeout(() => setSaveMessage(null), 3000);
  };

  const hasChanges = name !== organization?.name || description !== organization?.description;

  return (
    <div className="space-y-6">
      {/* Organization Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Organization Details</CardTitle>
          <CardDescription>Update your organization name and description</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Organization Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter organization name"
              disabled={!isAdmin}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your organization"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!isAdmin}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Organization Slug</label>
            <Input value={organization?.slug || ''} disabled className="bg-muted" />
            <p className="text-xs text-muted-foreground">
              This is your unique organization identifier and cannot be changed
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between border-t pt-4">
          <div>
            {saveMessage && (
              <p
                className={`text-sm ${
                  saveMessage.type === 'success' ? 'text-success' : 'text-destructive'
                }`}
              >
                {saveMessage.text}
              </p>
            )}
          </div>
          <Button onClick={handleSave} disabled={!hasChanges || saving || !isAdmin}>
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      {/* Organization Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Organization Info</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Created</span>
              <p className="font-medium">{formatDate(organization?.createdAt)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Updated</span>
              <p className="font-medium">{formatDate(organization?.updatedAt)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Organization ID</span>
              <p className="font-mono text-xs">{organization?.id}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Security Settings</CardTitle>
          <CardDescription>Manage organization security preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <p className="font-medium text-sm">Allow Member Invites</p>
              <p className="text-xs text-muted-foreground">
                Let admins invite new members to the organization
              </p>
            </div>
            <div
              className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                organization?.settings?.allowMemberInvites ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform mt-0.5 ${
                  organization?.settings?.allowMemberInvites ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <p className="font-medium text-sm">Require Two-Factor Authentication</p>
              <p className="text-xs text-muted-foreground">
                Enforce 2FA for all organization members
              </p>
            </div>
            <div
              className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                organization?.settings?.requireTwoFactor ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform mt-0.5 ${
                  organization?.settings?.requireTwoFactor ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-sm">SSO Authentication</p>
              <p className="text-xs text-muted-foreground">
                Enable Single Sign-On with your identity provider
              </p>
            </div>
            <span
              className={`text-xs px-2 py-1 rounded-full ${
                organization?.settings?.ssoEnabled
                  ? 'bg-success/10 text-success'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {organization?.settings?.ssoEnabled ? 'Enabled' : 'Not configured'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// API Keys Tab Component
// ============================================================================

function ApiKeysTab({ apiKeys, setApiKeys, orgId, featureConfig, isAdmin }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [deleteKeyId, setDeleteKeyId] = useState(null);
  const [rotatingKeyId, setRotatingKeyId] = useState(null);

  // Create key form state
  const [keyName, setKeyName] = useState('');
  const [keyScope, setKeyScope] = useState('read');
  const [keyExpiry, setKeyExpiry] = useState('');
  const [keyDescription, setKeyDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreateKey = async () => {
    if (!keyName.trim()) return;

    setCreating(true);
    const result = await createApiKey(orgId, {
      name: keyName,
      scope: keyScope,
      expiresInDays: keyExpiry ? parseInt(keyExpiry) : undefined,
      description: keyDescription,
    });
    setCreating(false);

    if (result.success) {
      setNewKey(result.data);
      setApiKeys((prev) => [result.data, ...prev]);
      setShowCreateModal(false);
      setShowKeyModal(true);
      // Reset form
      setKeyName('');
      setKeyScope('read');
      setKeyExpiry('');
      setKeyDescription('');
    }
  };

  const handleRotateKey = async (keyId) => {
    setRotatingKeyId(keyId);
    const result = await rotateApiKey(orgId, keyId);
    setRotatingKeyId(null);

    if (result.success) {
      setNewKey(result.data);
      setApiKeys((prev) =>
        prev.map((k) =>
          k.id === keyId
            ? { ...k, keyPrefix: result.data.keyPrefix, createdAt: result.data.createdAt }
            : k
        )
      );
      setShowKeyModal(true);
    }
  };

  const handleDeleteKey = async (keyId) => {
    const result = await deleteApiKey(orgId, keyId);
    if (result.success) {
      setApiKeys((prev) => prev.filter((k) => k.id !== keyId));
    }
    setDeleteKeyId(null);
  };

  const copyToClipboard = (text, keyId) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const activeKeys = apiKeys.filter((k) => k.status === 'active');
  const canCreateMore = featureConfig?.maxApiKeys === -1 || activeKeys.length < featureConfig?.maxApiKeys;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">API Keys</h2>
          <p className="text-sm text-muted-foreground">
            Manage API keys for programmatic access to Vedaya
          </p>
        </div>
        {featureConfig?.allowApiKeyCreation && isAdmin && (
          <Button onClick={() => setShowCreateModal(true)} disabled={!canCreateMore}>
            <Plus className="w-4 h-4" />
            Create API Key
          </Button>
        )}
      </div>

      {/* Key Count Warning */}
      {!canCreateMore && (
        <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
          <AlertCircle className="w-4 h-4" />
          <span>
            You have reached the maximum number of API keys ({featureConfig?.maxApiKeys}).
            Delete unused keys to create new ones.
          </span>
        </div>
      )}

      {/* API Keys List */}
      <Card>
        <CardContent className="p-0">
          {apiKeys.length === 0 ? (
            <div className="p-8 text-center">
              <Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No API Keys</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create an API key to get started with programmatic access
              </p>
              {featureConfig?.allowApiKeyCreation && isAdmin && (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4" />
                  Create Your First API Key
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {apiKeys.map((key) => (
                <div key={key.id} className="p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{key.name}</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full border ${getStatusBadgeClass(
                            key.status
                          )}`}
                        >
                          {key.status}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                          {key.scope}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <code className="px-2 py-0.5 bg-muted rounded text-xs font-mono">
                          {key.keyPrefix}
                        </code>
                        <button
                          onClick={() => copyToClipboard(key.keyPrefix, key.id)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {copiedKey === key.id ? (
                            <Check className="w-3.5 h-3.5 text-success" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                      {key.description && (
                        <p className="text-xs text-muted-foreground">{key.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Created {formatDate(key.createdAt)}</span>
                        {key.createdByName && <span>by {key.createdByName}</span>}
                        {key.lastUsedAt && <span>Last used {getTimeAgo(key.lastUsedAt)}</span>}
                        <span>{formatNumber(key.usageCount)} requests</span>
                        {key.expiresAt && (
                          <span
                            className={
                              getDaysUntil(key.expiresAt) < 7 ? 'text-warning' : ''
                            }
                          >
                            Expires {formatDate(key.expiresAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    {isAdmin && key.status === 'active' && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRotateKey(key.id)}
                          disabled={rotatingKeyId === key.id}
                        >
                          {rotatingKeyId === key.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                          Rotate
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setDeleteKeyId(key.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Key Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for programmatic access to Vedaya
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g., Production API Key"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Scope</label>
              <select
                value={keyScope}
                onChange={(e) => setKeyScope(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="read">Read Only</option>
                <option value="write">Read & Write</option>
                <option value="admin">Admin</option>
                <option value="full">Full Access</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Expiration (optional)</label>
              <select
                value={keyExpiry}
                onChange={(e) => setKeyExpiry(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Never expires</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="180">6 months</option>
                <option value="365">1 year</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description (optional)</label>
              <textarea
                value={keyDescription}
                onChange={(e) => setKeyDescription(e.target.value)}
                placeholder="What will this key be used for?"
                className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKey} disabled={!keyName.trim() || creating}>
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Key'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show New Key Modal */}
      <Dialog open={showKeyModal} onOpenChange={setShowKeyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
            <DialogDescription>
              Copy your new API key now. You will not be able to see it again.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <code className="text-sm font-mono break-all">{newKey?.key}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(newKey?.key, 'new')}
                >
                  {copiedKey === 'new' ? (
                    <Check className="w-4 h-4 text-success" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex gap-2 text-sm text-warning">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Make sure to copy your API key now. For security reasons, it will not be shown
                  again.
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowKeyModal(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteKeyId} onOpenChange={() => setDeleteKeyId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete API Key</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this API key? This action cannot be undone and any
              applications using this key will lose access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteKeyId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleDeleteKey(deleteKeyId)}>
              Delete Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Members Tab Component
// ============================================================================

function MembersTab({
  members,
  setMembers,
  invitations,
  setInvitations,
  orgId,
  currentUser,
  featureConfig,
  isAdmin,
}) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [removeMemberId, setRemoveMemberId] = useState(null);
  const [cancelInviteId, setCancelInviteId] = useState(null);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;

    setInviting(true);
    const result = await inviteMember(orgId, {
      email: inviteEmail,
      role: inviteRole,
    });
    setInviting(false);

    if (result.success) {
      setInvitations((prev) => [result.data, ...prev]);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
    }
  };

  const handleRemoveMember = async (memberId) => {
    const result = await removeMember(orgId, memberId);
    if (result.success) {
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    }
    setRemoveMemberId(null);
  };

  const handleCancelInvite = async (inviteId) => {
    const result = await cancelInvitation(orgId, inviteId);
    if (result.success) {
      setInvitations((prev) => prev.filter((i) => i.id !== inviteId));
    }
    setCancelInviteId(null);
  };

  const handleRoleChange = async (memberId, newRole) => {
    const result = await updateMember(orgId, memberId, { role: newRole });
    if (result.success) {
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
      );
    }
  };

  const canInviteMore =
    featureConfig?.maxMembers === -1 || members.length < featureConfig?.maxMembers;
  const currentUserMember = members.find((m) => m.email === currentUser?.email);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Team Members</h2>
          <p className="text-sm text-muted-foreground">
            Manage who has access to your organization
          </p>
        </div>
        {featureConfig?.allowMemberInvitation && isAdmin && (
          <Button onClick={() => setShowInviteModal(true)} disabled={!canInviteMore}>
            <Mail className="w-4 h-4" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Member Count */}
      {featureConfig?.maxMembers !== -1 && (
        <div className="flex items-center gap-2 text-sm">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span>
            {members.length} of {featureConfig?.maxMembers} seats used
          </span>
          {!canInviteMore && (
            <span className="text-warning text-xs">(Limit reached)</span>
          )}
        </div>
      )}

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pending Invitations</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {invitations.map((invite) => (
                <div key={invite.id} className="p-4 flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{invite.email}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${getRoleBadgeClass(
                          invite.role
                        )}`}
                      >
                        {invite.role}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Invited by {invite.invitedByName} - Expires {formatDate(invite.expiresAt)}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setCancelInviteId(invite.id)}
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Members List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Members ({members.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {members.map((member) => {
              const isCurrentUser = member.email === currentUser?.email;
              const isOwner = member.role === 'owner';
              const canModify = isAdmin && !isOwner && !isCurrentUser;

              return (
                <div key={member.id} className="p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        {member.avatarUrl ? (
                          <img
                            src={member.avatarUrl}
                            alt={member.fullName}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <UserIcon className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{member.fullName}</span>
                          {isCurrentUser && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                              You
                            </span>
                          )}
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border ${getRoleBadgeClass(
                              member.role
                            )}`}
                          >
                            {member.role === 'owner' && <Crown className="w-3 h-3 inline mr-1" />}
                            {member.role}
                          </span>
                          {member.twoFactorEnabled && (
                            <Shield className="w-3.5 h-3.5 text-success" title="2FA enabled" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Joined {formatDate(member.joinedAt)}</span>
                          {member.lastActiveAt && (
                            <span>Active {getTimeAgo(member.lastActiveAt)}</span>
                          )}
                          {member.invitedByName && <span>Invited by {member.invitedByName}</span>}
                        </div>
                      </div>
                    </div>
                    {canModify && (
                      <div className="flex items-center gap-2">
                        <select
                          value={member.role}
                          onChange={(e) => handleRoleChange(member.id, e.target.value)}
                          className="text-xs h-8 px-2 rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setRemoveMemberId(member.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Role Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-primary" />
                <span className="font-medium">Owner</span>
              </div>
              <p className="text-xs text-muted-foreground">Full access, billing, delete org</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <UserCog className="w-4 h-4 text-warning" />
                <span className="font-medium">Admin</span>
              </div>
              <p className="text-xs text-muted-foreground">Manage members, API keys, settings</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <UserIcon className="w-4 h-4 text-success" />
                <span className="font-medium">Member</span>
              </div>
              <p className="text-xs text-muted-foreground">Create investigations, use API</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">Viewer</span>
              </div>
              <p className="text-xs text-muted-foreground">View only access</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={!inviteEmail.trim() || inviting}>
              {inviting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation */}
      <Dialog open={!!removeMemberId} onOpenChange={() => setRemoveMemberId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this member from your organization? They will lose
              all access immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveMemberId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleRemoveMember(removeMemberId)}>
              Remove Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invitation Confirmation */}
      <Dialog open={!!cancelInviteId} onOpenChange={() => setCancelInviteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Invitation</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this invitation? The recipient will no longer be
              able to join using this link.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelInviteId(null)}>
              Keep Invitation
            </Button>
            <Button variant="destructive" onClick={() => handleCancelInvite(cancelInviteId)}>
              Cancel Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Usage Tab Component
// ============================================================================

function UsageTab({ usage }) {
  if (!usage) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading usage data...</p>
        </div>
      </div>
    );
  }

  const getProgressColor = (current, limit) => {
    if (limit === -1) return 'bg-primary'; // Unlimited
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return 'bg-destructive';
    if (percentage >= 75) return 'bg-warning';
    return 'bg-primary';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-success" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-destructive" />;
      default:
        return <Minus className="w-4 h-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold">Usage Overview</h2>
        <p className="text-sm text-muted-foreground">
          Current billing period: {formatDate(usage.periodStart)} - {formatDate(usage.periodEnd)}
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {usage.metrics.map((metric) => {
          const percentage =
            metric.limit === -1 ? 0 : Math.min(100, (metric.current / metric.limit) * 100);
          const isUnlimited = metric.limit === -1;

          return (
            <Card key={metric.name}>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">
                      {metric.displayName}
                    </span>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(metric.trend)}
                      {metric.trendPercentage !== undefined && metric.trendPercentage !== 0 && (
                        <span
                          className={`text-xs ${
                            metric.trend === 'up' ? 'text-success' : 'text-destructive'
                          }`}
                        >
                          {metric.trend === 'up' ? '+' : ''}
                          {metric.trendPercentage}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">
                      {typeof metric.current === 'number'
                        ? metric.current >= 1000
                          ? formatNumber(metric.current)
                          : metric.current.toLocaleString()
                        : metric.current}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      / {isUnlimited ? 'Unlimited' : formatNumber(metric.limit)} {metric.unit}
                    </span>
                  </div>
                  {!isUnlimited && (
                    <div className="space-y-1">
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${getProgressColor(
                            metric.current,
                            metric.limit
                          )}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{percentage.toFixed(1)}% used</span>
                        {percentage >= 90 && (
                          <span className="text-destructive">Near limit</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Usage Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Usage Tips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-3 h-3 text-primary" />
              </div>
              <div>
                <p className="font-medium">Monitor API Usage</p>
                <p className="text-muted-foreground text-xs">
                  Set up alerts to get notified when you approach your API call limits
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-3 h-3 text-primary" />
              </div>
              <div>
                <p className="font-medium">Usage Resets Monthly</p>
                <p className="text-muted-foreground text-xs">
                  Your usage limits reset at the beginning of each billing cycle
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Billing Tab Component
// ============================================================================

function BillingTab({ orgId, featureConfig, isAdmin }) {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);

  // Load billing data
  useEffect(() => {
    loadBillingData();

    // Check for Stripe checkout result
    const checkoutResult = checkCheckoutResult();
    if (checkoutResult.success) {
      setMessage({ type: 'success', text: 'Payment successful! Your subscription is now active.' });
      setTimeout(() => setMessage(null), 5000);
    } else if (checkoutResult.canceled) {
      setMessage({ type: 'info', text: 'Checkout was canceled. No changes were made.' });
      setTimeout(() => setMessage(null), 5000);
    }
  }, []);

  const loadBillingData = async () => {
    setLoading(true);
    try {
      const [subData, plansData, pmData, invData] = await Promise.all([
        getStripeSubscription(),
        getStripePlans(),
        getStripePaymentMethods(),
        getStripeInvoices(10),
      ]);
      setSubscription(subData);
      setPlans(plansData);
      setPaymentMethods(pmData);
      setInvoices(invData);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (plan.tier === 'free' || !plan.price_id_monthly) {
      setMessage({ type: 'info', text: 'This plan does not require payment.' });
      return;
    }

    setProcessing(true);
    const priceId = billingCycle === 'yearly' ? plan.price_id_yearly : plan.price_id_monthly;
    const result = await createCheckoutSession(priceId);
    setProcessing(false);

    if (!result.success) {
      setMessage({ type: 'error', text: result.error || 'Failed to start checkout' });
    }
    // If successful, user will be redirected to Stripe Checkout
  };

  const handleManageBilling = async () => {
    setProcessing(true);
    const result = await openCustomerPortal();
    setProcessing(false);

    if (!result.success) {
      setMessage({ type: 'error', text: result.error || 'Failed to open billing portal' });
    }
    // If successful, user will be redirected to Stripe Customer Portal
  };

  const handleCancelSubscription = async () => {
    setProcessing(true);
    const result = await cancelStripeSubscription();
    setProcessing(false);

    if (result.success) {
      setSubscription((prev) => prev ? { ...prev, cancel_at_period_end: true } : null);
      setShowCancelModal(false);
      setMessage({ type: 'success', text: 'Subscription will be canceled at the end of the billing period.' });
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to cancel subscription' });
    }
  };

  const handleReactivate = async () => {
    setProcessing(true);
    const result = await reactivateSubscription();
    setProcessing(false);

    if (result.success) {
      setSubscription((prev) => prev ? { ...prev, cancel_at_period_end: false } : null);
      setMessage({ type: 'success', text: 'Subscription reactivated successfully!' });
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to reactivate subscription' });
    }
  };

  const handleSetDefaultPayment = async (paymentMethodId) => {
    const result = await setDefaultPaymentMethod(paymentMethodId);
    if (result.success) {
      setPaymentMethods((prev) =>
        prev.map((pm) => ({ ...pm, is_default: pm.id === paymentMethodId }))
      );
      setMessage({ type: 'success', text: 'Default payment method updated.' });
    } else {
      setMessage({ type: 'error', text: result.error });
    }
  };

  const handleDeletePayment = async (paymentMethodId) => {
    const result = await deletePaymentMethod(paymentMethodId);
    if (result.success) {
      setPaymentMethods((prev) => prev.filter((pm) => pm.id !== paymentMethodId));
      setMessage({ type: 'success', text: 'Payment method removed.' });
    } else {
      setMessage({ type: 'error', text: result.error });
    }
  };

  // Find current plan from subscription
  const currentPlan = plans.find((p) => {
    if (!subscription) return p.tier === 'free';
    return p.price_id_monthly === subscription.plan_id || p.price_id_yearly === subscription.plan_id;
  }) || plans.find((p) => p.tier === 'free');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Message */}
      {message && (
        <div
          className={`p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-success/10 text-success border border-success/20'
              : message.type === 'error'
              ? 'bg-destructive/10 text-destructive border border-destructive/20'
              : 'bg-primary/10 text-primary border border-primary/20'
          }`}
        >
          {message.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : message.type === 'error' ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            className="ml-auto text-current opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Current Plan</CardTitle>
              <CardDescription>Your current subscription details</CardDescription>
            </div>
            <div className="flex gap-2">
              {subscription && isAdmin && (
                <Button variant="outline" onClick={handleManageBilling} disabled={processing}>
                  <ExternalLink className="w-4 h-4" />
                  Manage Billing
                </Button>
              )}
              {featureConfig?.allowPlanChanges && isAdmin && (
                <Button onClick={() => setShowPlanModal(true)}>
                  {subscription ? 'Change Plan' : 'Upgrade'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{currentPlan?.name || 'Free'}</h3>
                  <p className="text-sm text-muted-foreground">{currentPlan?.description}</p>
                </div>
              </div>
              {subscription ? (
                <>
                  <div className="flex items-center gap-4">
                    <div className="text-2xl font-bold">
                      {formatAmount(subscription.amount, subscription.currency)}
                      <span className="text-sm font-normal text-muted-foreground">
                        /{subscription.billing_cycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                    {subscription.billing_cycle === 'yearly' && (
                      <span className="text-xs px-2 py-1 rounded-full bg-success/10 text-success">
                        Annual billing
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded-full ${getStatusBadgeClass(subscription.status)}`}
                    >
                      {subscription.status}
                    </span>
                    {subscription.cancel_at_period_end && (
                      <span className="text-warning">Cancels at period end</span>
                    )}
                    <span className="text-muted-foreground">
                      {subscription.cancel_at_period_end ? 'Ends' : 'Renews'}{' '}
                      {formatDate(subscription.current_period_end)}
                    </span>
                  </div>
                  {subscription.cancel_at_period_end && isAdmin && (
                    <Button variant="outline" size="sm" onClick={handleReactivate} disabled={processing}>
                      Reactivate Subscription
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  You are on the free plan. Upgrade to unlock more features.
                </div>
              )}
            </div>
            <div className="hidden md:block border-l border-border pl-6">
              <h4 className="text-sm font-medium mb-2">Plan Includes</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {currentPlan?.features?.slice(0, 4).map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-success" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
        {isAdmin && subscription?.status === 'active' && !subscription?.cancel_at_period_end && (
          <CardFooter className="border-t pt-4">
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setShowCancelModal(true)}
            >
              Cancel Subscription
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* Payment Methods */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Payment Methods</CardTitle>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={handleManageBilling} disabled={processing}>
                <Plus className="w-4 h-4" />
                Manage Cards
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {paymentMethods.length === 0 ? (
            <p className="text-muted-foreground">No payment methods on file</p>
          ) : (
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className="flex items-center justify-between p-3 border border-border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-7 rounded bg-muted flex items-center justify-center">
                      <span className="text-xs font-bold uppercase">
                        {getCardBrandName(method.card_brand)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        **** **** **** {method.card_last4}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Expires {method.card_exp_month}/{method.card_exp_year}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {method.is_default ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                        Default
                      </span>
                    ) : isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefaultPayment(method.id)}
                      >
                        Set Default
                      </Button>
                    )}
                    {isAdmin && !method.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeletePayment(method.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Billing History</CardTitle>
            {invoices.length > 0 && isAdmin && (
              <Button variant="outline" size="sm" onClick={handleManageBilling}>
                View All Invoices
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {invoices.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">No invoices yet</div>
          ) : (
            <div className="divide-y divide-border">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="p-4 flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{invoice.number || invoice.id}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          invoice.status === 'paid'
                            ? 'bg-success/10 text-success'
                            : 'bg-warning/10 text-warning'
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium">
                      {formatAmount(invoice.amount_due, invoice.currency)}
                    </span>
                    {invoice.pdf_url && (
                      <a
                        href={invoice.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        <Download className="w-4 h-4" />
                        PDF
                      </a>
                    )}
                    {invoice.hosted_invoice_url && (
                      <a
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Plan Modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Choose a Plan</DialogTitle>
            <DialogDescription>
              Select a plan that works best for your team. You will be redirected to Stripe to complete payment.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {/* Billing Cycle Toggle */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  billingCycle === 'yearly'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs bg-success text-success-foreground px-2 py-0.5 rounded-full">
                  Save 17%
                </span>
              </button>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan) => {
                const isCurrentPlan = plan.id === currentPlan?.id;
                const price = billingCycle === 'yearly'
                  ? (plan.yearly_price || 0) / 12
                  : (plan.monthly_price || 0);
                const priceId = billingCycle === 'yearly'
                  ? plan.price_id_yearly
                  : plan.price_id_monthly;

                return (
                  <div
                    key={plan.id}
                    onClick={() => !isCurrentPlan && priceId && setSelectedPlan(plan)}
                    className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedPlan?.id === plan.id
                        ? 'border-primary bg-primary/5'
                        : isCurrentPlan
                        ? 'border-muted bg-muted/50 cursor-not-allowed'
                        : !priceId
                        ? 'border-border opacity-60 cursor-not-allowed'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {plan.is_popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                          Popular
                        </span>
                      </div>
                    )}
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-semibold">{plan.name}</h3>
                        <p className="text-xs text-muted-foreground">{plan.description}</p>
                      </div>
                      <div>
                        <span className="text-2xl font-bold">{formatAmount(price * 100)}</span>
                        <span className="text-sm text-muted-foreground">/mo</span>
                      </div>
                      <ul className="space-y-1">
                        {plan.features?.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-xs">
                            <Check className="w-3 h-3 text-success" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {isCurrentPlan && (
                        <span className="block text-center text-xs text-muted-foreground">
                          Current Plan
                        </span>
                      )}
                      {!priceId && plan.tier !== 'free' && (
                        <span className="block text-center text-xs text-muted-foreground">
                          Contact Sales
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => selectedPlan && handleSelectPlan(selectedPlan)}
              disabled={!selectedPlan || processing}
            >
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Continue to Checkout
                  <ExternalLink className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? Your access will continue
              until the end of your current billing period (
              {subscription ? formatDate(subscription.current_period_end) : ''}).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-warning">What happens when you cancel:</p>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    <li>Your team will lose access at the end of the billing period</li>
                    <li>All data will be retained for 30 days</li>
                    <li>You can reactivate anytime before the period ends</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={processing}
            >
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Canceling...
                </>
              ) : (
                'Cancel Subscription'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
